import React from 'react';
import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {
	FontAwesomeIcon,
	type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import MuiButton, {
	buttonClasses,
	type ButtonTypeMap,
	type ButtonProps as MuiButtonProps,
} from '@mui/material/Button';
import {styled} from '@netx/core-components/Theme/styled';

export {buttonClasses} from '@mui/material/Button';

const ButtonRoot = styled(MuiButton)(({theme, size}) => ({
	...(size === 'small'
		? {
				[`& .${buttonClasses.endIcon}, & .${buttonClasses.startIcon}`]: {
					'& svg': {
						width: 12,
					},
				},
			}
		: null),
}));

export type ButtonProps<RootComponent extends React.ElementType = 'button'> =
	Omit<MuiButtonProps<RootComponent>, 'endIcon' | 'startIcon' | 'onClick'> & {
		endIcon?: IconProp;
		startIcon?: IconProp;
		EndIconProps?: Omit<Partial<FontAwesomeIconProps>, 'icon'> & {
			'data-testid'?: string;
		};
		StartIconProps?: Omit<Partial<FontAwesomeIconProps>, 'icon'> & {
			'data-testid'?: string;
		};

		// TODO: without this the event gets lost in a component tha calls this
		onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
	};

export const ButtonPrimitive = <
	RootComponent extends React.ElementType = 'button',
>(
	{
		children,
		endIcon,
		startIcon,
		EndIconProps,
		StartIconProps,
		...props
	}: ButtonProps<RootComponent>,
	ref: React.ForwardedRef<HTMLButtonElement>,
) => {
	return (
		// @ts-ignore
		<ButtonRoot<RootComponent>
			{...props}
			ref={ref}
			endIcon={
				endIcon && (
					<FontAwesomeIcon
						data-testid="button-end-icon"
						fixedWidth
						{...EndIconProps}
						icon={endIcon}
					/>
				)
			}
			startIcon={
				startIcon && (
					<FontAwesomeIcon
						data-testid="button-start-icon"
						fixedWidth
						{...StartIconProps}
						icon={startIcon}
					/>
				)
			}
		>
			{typeof children === 'string' ? (
				<span data-testid="action-button-label">{children}</span>
			) : (
				children
			)}
		</ButtonRoot>
	);
};

export const Button = React.forwardRef(ButtonPrimitive) as <
	RootComponent extends React.ElementType = 'button',
>(
	props: ButtonProps<RootComponent>,
) => JSX.Element;

export default Button;
